import throttle from 'lodash/throttle';
import React, { createContext, use, useCallback, useEffect, useMemo, useState } from 'react';

// Types
interface Size {
    width: number;
    height: number;
}

interface Scroll {
    x: number;
    y: number;
}

interface LayoutState {
    size: Size;
    scroll: Scroll;
    fontsLoaded: boolean;
    menuOpened: boolean;
    popupClosed: boolean;
    footerIsPurple: boolean;
}

interface LayoutContextValue extends LayoutState {
    setSize: (size: Size) => void;
    setScroll: (scroll: Scroll) => void;
    setFontsLoaded: (loaded: boolean) => void;
    setMenuOpened: (opened: boolean) => void;
    setPopupClosed: (closed: boolean) => void;
    setFooterIsPurple: (isPurple: boolean) => void;
}

// Helper function to get current scroll
const getScroll = (): Scroll => ({
    x: window.pageXOffset || document.documentElement.scrollLeft || document.body.scrollLeft || 0,
    y: window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0,
});

// Context
const LayoutContext = createContext<LayoutContextValue | undefined>(undefined);

// Provider Props
interface LayoutProviderProps {
    children: React.ReactNode;
    size?: Size;
    scroll?: Scroll;
    fontsLoaded?: boolean;
    menuOpened?: boolean;
    popupClosed?: boolean;
    footerIsPurple?: boolean;
}

// Provider Component
export const LayoutProvider: React.FC<LayoutProviderProps> = ({
    children,
    size: initialSize = null,
    scroll: initialScroll = null,
    fontsLoaded: initialFontsLoaded = false,
    menuOpened: initialMenuOpened = false,
    popupClosed: initialPopupClosed = false,
    footerIsPurple: initialFooterIsPurple = false,
}) => {
    const [size, setSize] = useState<Size>(
        initialSize || {
            width: typeof window !== 'undefined' ? window.innerWidth : 0,
            height: typeof window !== 'undefined' ? window.innerHeight : 0,
        },
    );
    const [scroll, setScroll] = useState<Scroll>(
        initialScroll || (typeof window !== 'undefined' ? getScroll() : { x: 0, y: 0 }),
    );
    const [fontsLoaded, setFontsLoaded] = useState<boolean>(initialFontsLoaded);
    const [menuOpened, setMenuOpened] = useState<boolean>(initialMenuOpened);
    const [popupClosed, setPopupClosed] = useState<boolean>(initialPopupClosed);
    const [footerIsPurple, setFooterIsPurple] = useState<boolean>(initialFooterIsPurple);

    // Auto-update size on window resize
    useEffect(() => {
        const handleResize = () => {
            const newSize = {
                width: window.innerWidth,
                height: window.innerHeight,
            };
            setSize(newSize);
            // Close menu if transitioning from mobile to desktop
            if (size.width < 760 && newSize.width >= 760) {
                setMenuOpened(false);
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [size.width]);

    // Auto-update scroll on window scroll (throttled for performance)
    useEffect(() => {
        const handleScroll = throttle(
            () => {
                setScroll(getScroll());
            },
            1000 / 20, // 20fps
            { leading: true, trailing: true },
        );

        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => {
            window.removeEventListener('scroll', handleScroll);
            handleScroll.cancel();
        };
    }, []);

    const handleSetSize = useCallback(
        (newSize: Size) => {
            setSize(newSize);
            // Close menu if transitioning from mobile to desktop
            if (size.width < 760 && newSize.width >= 760) {
                setMenuOpened(false);
            }
        },
        [size.width],
    );

    const value = useMemo(
        () => ({
            size,
            scroll,
            fontsLoaded,
            menuOpened,
            popupClosed,
            footerIsPurple,
            setSize: handleSetSize,
            setScroll,
            setFontsLoaded,
            setMenuOpened,
            setPopupClosed,
            setFooterIsPurple,
        }),
        [
            size,
            scroll,
            fontsLoaded,
            menuOpened,
            popupClosed,
            footerIsPurple,
            handleSetSize,
            setScroll,
            setFontsLoaded,
            setMenuOpened,
            setPopupClosed,
            setFooterIsPurple,
        ],
    );

    return <LayoutContext value={value}>{children}</LayoutContext>;
};

// Hook
export const useLayout = (): LayoutContextValue => {
    const context = use(LayoutContext);
    if (context === undefined) {
        throw new Error('useLayout must be used within a LayoutProvider');
    }
    return context;
};

export default LayoutContext;
